import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { deskReviewKeys } from "@/hooks/admin/deskReviews";
import { api } from "@/services/api";
import type { Page, UserRole, UserWithEnrollment } from "@/types";

/**
 * React Query hooks for the admin Participants feature (users-with-enrollments
 * list). Wraps the existing API client method — the client is unchanged — and
 * owns the query key + caching that previously lived as a manual `useEffect`
 * loader in the page. List params are baked into the key, so changing
 * page/search/sort/role refetches naturally. Follows the Sources slice template.
 */

export interface ParticipantsListParams {
  page: number;
  page_size: number;
  sort_by: "email" | "created_at" | "role";
  sort_order: "asc" | "desc";
  q?: string;
  role?: UserRole;
}

export const participantKeys = {
  all: ["admin", "participants"] as const,
  lists: () => [...participantKeys.all, "list"] as const,
  list: (params: ParticipantsListParams) => [...participantKeys.lists(), params] as const,
};

export function useParticipants(params: ParticipantsListParams) {
  return useQuery<Page<UserWithEnrollment>>({
    queryKey: participantKeys.list(params),
    queryFn: () => api.getUsersWithEnrollments(params),
  });
}

/** Grant or revoke the Desk review approver permission (KC-509). */
export function useSetApprover() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, isApprover }: { userId: string; isApprover: boolean }) =>
      api.setApprover(userId, isApprover),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: participantKeys.all });
      queryClient.invalidateQueries({ queryKey: deskReviewKeys.summary() });
    },
  });
}
